import * as React from "react";

import { TagInput, type TextInputSize } from "@/shared/foundation/primitives";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import {
  type FilterOptions,
  type FilterOptionsResolver,
  type LabeledOption,
} from "../FilterDefinition";
import {
  type EnumComparator,
  type SetComparator,
  isValueless,
} from "../FilterSchema";

interface TagsValueInputProps {
  comparator: SetComparator | EnumComparator;
  /** The selected values. Tags are their own label, so both fields match. */
  value: LabeledOption[];
  /** Fired on every change with the next selected values. */
  onChange: (next: LabeledOption[]) => void;
  /** The suggestion source, in either of {@link FilterOptions}' forms. */
  options: FilterOptions;
  /** Field size, on the `TextInput` scale. @default "md" */
  size?: TextInputSize;
  /** Marks the field invalid, for an error shown beside it. */
  invalid?: boolean;
}

/** The values a resolver returned, whichever shape it chose to report them in. */
function resolvedValues(
  resolved: Awaited<ReturnType<FilterOptionsResolver>>,
): string[] {
  const options = Array.isArray(resolved) ? resolved : resolved.options;
  return (options as Array<string | LabeledOption>).map((option) =>
    typeof option === "string" ? option : option.value,
  );
}

/**
 * The value input for a ``set`` field whose values are authored, not chosen:
 * a tag field whose pills are the selected values and whose popup suggests
 * what the option source knows, with the typed text always on offer beneath
 * them.
 *
 * Unlike ``OptionsValueInput``, a value the source doesn't list is a normal
 * outcome here — a trigger may legitimately name a tag nothing carries yet.
 * Labels are therefore the values themselves; a field with opaque ids and
 * friendly labels belongs in the checkbox picker instead.
 */
export function TagsValueInput({
  comparator,
  value,
  onChange,
  options,
  size = "md",
  invalid = false,
}: TagsValueInputProps) {
  const suggestions = React.useMemo(() => {
    if (typeof options !== "function") {
      return resolvedValues(options);
    }
    // Reported here, at the app boundary, rather than inside the primitive:
    // the checkbox picker got this for free from the query cache's `onError`,
    // and a tag search failing in a trigger form should be just as visible.
    // An abort is the field moving on, not a failure.
    return async (query: string, signal: AbortSignal) => {
      try {
        return resolvedValues(await options(query, signal));
      } catch (error) {
        if (!signal.aborted) {
          ErrorMonitoringService.captureError(error, { query });
        }
        throw error;
      }
    };
    // The resolver is called through a ref inside `TagInput`, so rebuilding
    // this wrapper per render is harmless — but memoizing keeps the static
    // array stable too.
  }, [options]);

  if (isValueless(comparator)) {
    return null;
  }

  return (
    <TagInput
      tags={value.map((option) => option.value)}
      onChange={(tags) =>
        onChange(tags.map((tag) => ({ label: tag, value: tag })))
      }
      suggestions={suggestions}
      size={size}
      aria-invalid={invalid || undefined}
      aria-label="Values"
      placeholder="Add values…"
    />
  );
}
